import {
    MethodObserver,
    Observers,
    SignalRMethod
} from "@api/repositories/reatime-repository/types";
import {isChannelForTheMessage} from "@api/repositories/reatime-repository/helpers/realtimeChannelMatcher";
import {ChannelSubscriptionCounter} from "@api/repositories/reatime-repository/ChannelSubscriptionCounter";
import {RealtimeConnection} from "@api/repositories/reatime-repository/RealtimeConnection";

export class ObserversManager {
    observers: { [key in SignalRMethod]: MethodObserver[] } = {} as any;

    constructor(
        private readonly connection: RealtimeConnection,
        private readonly subscriptionCounter: ChannelSubscriptionCounter
    ) {
    }

    /**
     * Attach message handler
     * Attaching happens if there are no prior observers or forceAttaching is passed as true
     * @param method
     * @param forceAttaching
     * @private
     */
    private attachMessageHandlerToMethod(method: SignalRMethod, forceAttaching = false) {
        if (!this.observers[method].length || forceAttaching) {
            this.connection.hub.on(method, this.messageHandler(method));
        }
    }

    /**
     * Init empty observers list for the given method
     * @param method
     * @private
     */
    private initObserversList(method: SignalRMethod) {
        if (!this.observers[method]) {
            this.observers[method] = [];
        }
    }

    /**
     * Attach new observers
     * @param observers
     */
    async attach(observers: Observers) {
        for (let [channelName, methods] of Object.entries(observers)) {
            Object.keys(methods).forEach((signalRMethod: string) => {
                this.initObserversList(signalRMethod as SignalRMethod);
                this.attachMessageHandlerToMethod(signalRMethod as SignalRMethod);
                this.observers[signalRMethod as SignalRMethod].push({
                    channel: channelName,
                    callback: methods[signalRMethod as SignalRMethod]
                });

                this.subscriptionCounter?.inc(channelName);
            })
        }
    }

    /**
     * Detach observers
     * @param observers
     */
    async detach(observers: Observers) {
        const channelsToUnsubscribe = [];

        for (let [channelName, methods] of Object.entries(observers)) {
            Object.keys(methods).forEach((signalRMethod: string) => {
                if (this.observers[signalRMethod as SignalRMethod]) {
                    const observerIndex = this.observers[signalRMethod as SignalRMethod]
                        .findIndex(item => item.channel == channelName && item.callback == methods[signalRMethod as SignalRMethod]);
                    if (observerIndex >= 0) {
                        this.observers[signalRMethod as SignalRMethod].splice(observerIndex, 1);
                        this.subscriptionCounter?.dec(channelName);

                        if (this.observers[signalRMethod as SignalRMethod].length <= 0) {
                            this.connection.hub.off(signalRMethod);
                        }
                    }
                }
            });

            if (this.subscriptionCounter?.hasChannel(channelName) && !this.subscriptionCounter?.hasSubscriptions(channelName)) {
                channelsToUnsubscribe.push(channelName);
            }
        }

        return channelsToUnsubscribe;
    }

    /**
     * Message handler - receive all of the messages and runs notify
     * @param method
     */
    private messageHandler = (method: SignalRMethod) => (message: any) => {
        this.notify(method, message);
    }

    /**
     * Search for the corresponding observers and runs them
     * @param method
     * @param message
     * @private
     */
    private notify(method: SignalRMethod, message: any) {
        const isMessageObject = typeof message === 'object' && message !== null;
        const isArray = Array.isArray(message);

        for (const observer of this.observers[method]) {
            const messageClone = isMessageObject ?
                (isArray ? [...message] : {...message})
                : message;

            if (isChannelForTheMessage(method, observer, messageClone)) {
                observer.callback(messageClone);
            }
        }
    }

    /**
     * Restore observers in case there was a connection failure
     */
    async restore() {
        Object.keys(this.observers).forEach((method) => {
            this.attachMessageHandlerToMethod(method as SignalRMethod, true);
        });
    }

    /**
     * Returns the list of the observing channels
     */
    getChannels() {
        return Object.values(this.observers).reduce((accumulator: string[], currentValue) => {
            return [...accumulator, ...currentValue.map(observer => observer.channel)];
        }, [])
    }

    /**
     * Send message to all observers
     * @param message
     */
    notifyAll(message: any) {
        Object.values(this.observers).forEach(observers => {
            observers.forEach(observer => {
                observer.callback(JSON.stringify(message));
            })
        });
    }

    /**
     * Detach all message handlers from the hub
     */
    async detachAll() {
        Object.keys(this.observers).forEach((method) => {
            this.connection.hub.off(method);
        });
        this.observers = {} as any;
    }
}
